import React, { useEffect, useState } from "react";
import PogTable from "../POGTable/POGTable";
import PogForm from "../POGForm/POGForm";
import Dialog from "../../../FormDialog/Dialog";
import ErrorDialog from "../../../ErrorDialog/ErrorDialog";
import { getUserInfo } from "../../../../../services/authService-sighup";
import { getHeaders } from "../../../../ApiHeaders/apiHeadersEntitlements";
import { SplitDivider, useResizableSplit } from "../../../../SplitPane/ResizableSplit";
import LoadingStyle from "../../../../Loading/Loading";
import { isAmpEntitlementDomain } from "../../../constants/entitlementDomains";

const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;

// Offer only permission objects whose service is in amp/mobile
// (PERMISSION_OBJECT.SERVICE_NAME -> SERVICE.DOMAIN). If the service list or
// its domain is unavailable, fall back to offering every object.
const filterToAmpServices = async (poList: any[], headers: any) => {
  try {
    const serviceRes = await fetch(`${apiUrl}/service`, { headers });
    if (!serviceRes.ok) return poList;

    const serviceJson = await serviceRes.json();
    const serviceDomain: Record<string, string | undefined> = {};
    (serviceJson.response?.data || []).forEach((s: any) => {
      serviceDomain[s.service_name] = s.domain;
    });

    return poList.filter((po: any) => {
      const domain = serviceDomain[po.service_name];
      return domain === undefined || domain === null || isAmpEntitlementDomain(domain);
    });
  } catch {
    return poList;
  }
};

interface PogSetupProps {
    registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
    canDelete?: boolean;
  }

const PogSetup: React.FC<PogSetupProps> = ({
  registerTabSwitchGuard,
  canDelete,
}) => {
  const [pogs, setPogs] = useState<any[]>([]);
  const [selectedPog, setSelectedPog] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [permissionObjects, setPermissionObjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [error, setError] = useState<any>(null);

  const [user, setUser] = useState<any>(null);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  useEffect(() => {
    const userInfo = getUserInfo();
    if (userInfo) {
      setUser({
        fullName: userInfo.fullName,
        email: userInfo.email,
      });
    }
  }, []);

  const createdBy = user?.email || "system";
  const modifiedBy = user?.email || "system";

  // -----------------------------------------------------
  // FETCH ALL DATA
  // -----------------------------------------------------
  const fetchData = async () => {
    setLoading(true);
    const headers = await getHeaders();

    try {
      const pogRes = await fetch(`${apiUrl}/permission-object-group`, { headers });
      const pogJson = await pogRes.json();
      const pogList = (pogJson.response?.data || []).filter((pog: any) =>
        isAmpEntitlementDomain(pog.domain)
      );

      const poRes = await fetch(`${apiUrl}/permission-object`, { headers });
      const poJson = await poRes.json();
      const poList = poJson.response?.data || [];

      const mappingRes = await fetch(`${apiUrl}/pog-po-mapping`, { headers });
      const mappingJson = await mappingRes.json();
      const mappings = mappingJson.response?.data || [];

      const poMap: Record<number, any> = {};
      poList.forEach((po: any) => {
        poMap[po.permission_object_id] = po;
      });

      const grouped: Record<number, any> = {};

      pogList.forEach((pog: any) => {
        grouped[pog.permission_object_group_id] = {
          ...pog,
          permission_objects: [],
        };
      });

      mappings.forEach((m: any) => {
        const pogId = m.permission_object_group_id;
        const poId = m.permission_object_id;

        if (grouped[pogId] && poMap[poId]) {
          grouped[pogId].permission_objects.push({
            permission_object_id: poId,
            permission_object_name:
              poMap[poId].permission_object_name,
          });
        }
      });

      setPogs(Object.values(grouped));
      setPermissionObjects(await filterToAmpServices(poList, headers));

    } catch (err) {
      setError({
        title: "Failed to Fetch Data",
        details: "Error while fetching POG data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // -----------------------------------------------------
  // CREATE
  // -----------------------------------------------------
  const handleCreate = async (newPog: any) => {
    const headers = await getHeaders();

    try {
      const response = await fetch(`${apiUrl}/permission-object-group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          ...newPog,
          sys_created_by: createdBy,
          sys_modified_by: modifiedBy,
        }),
      });

      const json = await response.json();
      const newId = json.response?.data?.permission_object_group_id;

      // create mappings
      for (const poId of newPog.permission_object_ids) {
        await fetch(`${apiUrl}/pog-po-mapping`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
          body: JSON.stringify({
            permission_object_group_id: newId,
            permission_object_id: poId,
            enabled: "Y",
            sys_created_by: createdBy,
            sys_modified_by: modifiedBy,
          }),
        });
      }

      await fetchData();
    } catch (err) {
      setError({
        title: "Failed to Create",
        details: "Error while creating POG.",
      });
    }
  };

  // -----------------------------------------------------
  // UPDATE
  // -----------------------------------------------------
//   const handleUpdate = async (id: number, updated: any) => {
//     const headers = await getHeaders();

//     try {
//       await fetch(`${apiUrl}/permission-object-group/${id}`, {
//         method: "PUT",
//         headers: {
//           "Content-Type": "application/json",
//           ...headers,
//         },
//         body: JSON.stringify(updated),
//       });

//       // refresh mappings (simple approach: refetch everything)
//       await fetchData();
//     } catch (err) {
//       setError({
//         title: "Failed to Update",
//         details: "Error while updating POG.",
//       });
//     }
//   };
const handleUpdate = async (id: number, updated: any) => {
    const headers = await getHeaders();
  
    try {
      // STEP 1: Update POG
      await fetch(`${apiUrl}/permission-object-group/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          pog_name: updated.pog_name,
          domain: updated.domain,
          description: updated.description,
          enabled: updated.enabled,
          sys_modified_by: updated.sys_modified_by || "system",
        }),
      });
  
      // STEP 2: Get original POG
      const original = pogs.find(
        (p) => p.permission_object_group_id === id
      );
  
      const originalIds =
        original?.permission_objects?.map(
          (po: any) => po.permission_object_id
        ) || [];
  
      const updatedIds = updated.permission_object_ids || [];
  
      // STEP 3: Diff
      const added = updatedIds.filter(
        (poId: number) => !originalIds.includes(poId)
      );
  
      const removed = originalIds.filter(
        (poId: number) => !updatedIds.includes(poId)
      );
  
      // STEP 4: Add new mappings
      for (const poId of added) {
        await fetch(`${apiUrl}/pog-po-mapping`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers,
          },
          body: JSON.stringify({
            permission_object_group_id: id,
            permission_object_id: poId,
            enabled: "Y",
            sys_created_by: "system",
            sys_modified_by: "system",
          }),
        });
      }
  
      // STEP 5: Delete removed mappings
      for (const poId of removed) {
        await fetch(
          `${apiUrl}/pog-po-mapping/${id}/${poId}`,
          {
            method: "DELETE",
            headers,
          }
        );
      }
  
      await fetchData();
    } catch (err) {
      setError({
        title: "Failed to Update",
        details: "Error while updating POG.",
      });
    }
  };

  const handleDeletePog = async (pogId: number) => {
    const headers = await getHeaders();
  
    try {
      // STEP 1 — Fetch mappings for this POG
      const mappingRes = await fetch(
        `${apiUrl}/pog-po-mapping/pog/${pogId}`,
        { headers }
      );
  
      if (!mappingRes.ok) {
        throw new Error("Failed to fetch POG mappings");
      }
  
      const mappingJson = await mappingRes.json();
  
      const mappings = mappingJson?.response?.data || [];
  
      // STEP 2 — Delete each mapping
      await Promise.all(
        mappings.map((m: any) =>
          fetch(
            `${apiUrl}/pog-po-mapping/${pogId}/${m.permission_object_id}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // STEP 3 — Delete the POG itself
      const deletePog = await fetch(
        `${apiUrl}/permission-object-group/${pogId}`,
        {
          method: "DELETE",
          headers,
        }
      );
  
      if (!deletePog.ok) {
        throw new Error("Failed to delete POG");
      }
  
      // STEP 4 — Refresh UI
      await fetchData();
  
      setSelectedPog(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(false);

      setDialog({
        open: true,
        title: "Deleted",
        message: "POG deleted successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      setError({
        title: "Failed to Delete",
        details: "Error while deleting POG.",
      });
    }
  };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <PogTable
          pogs={pogs}
          selectedRow={selectedRow}
          onRowSelect={(pog) => {
            setSelectedPog(pog);
            setSelectedRow(pog.permission_object_group_id);
          }}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <PogForm
          selectedPog={selectedPog}
          permissionObjects={permissionObjects}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          pogs={pogs}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === "create")}
          onDelete={handleDeletePog}
          canDelete={canDelete}
        />
      </div>

      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default PogSetup;